"use client";
import {
  alpha,
  createTheme,
  darken,
  lighten,
  Theme,
} from "@mui/material/styles";
import { commonTheme, commonTypography } from "@/themes/common/theme";
import { FONT_SANS_STACK } from "@/themes/shared/fontStacks";

// Each semantic palette color carries a low-opacity `background` tint paired
// with an accessible `contrastText`, consumed by the soft Chip styling below.
declare module "@mui/material/styles" {
  interface PaletteColor {
    background?: string;
  }
  interface SimplePaletteColorOptions {
    background?: string;
  }
}

export interface ThemeColors {
  primary: string;
  primaryFg: string;
  background: string;
  foreground: string;
  secondary: string;
  secondaryFg: string;
  accent: string;
  card: string;
  cardFg: string;
  mutedBg: string;
  mutedFg: string;
  border: string;
  input: string;
  destructive: string;
  destructiveBg: string;
  destructiveFg: string;
  error: string;
  errorBg: string;
  errorFg: string;
  info: string;
  infoBg: string;
  infoFg: string;
  success: string;
  successBg: string;
  successFg: string;
  warning: string;
  warningBg: string;
  warningFg: string;
  linkFg: string;
  linkHoverFg: string;
  sidebarBg: string;
  sidebarFg: string;
  dataPlaneSidebarBg: string;
  appBarBg: string;
  appBarFg: string;
  tabsFg: string;
  tabsBg: string;
  tabsInactiveFg?: string;
}

export function createCustomTheme(
  mode: "light" | "dark",
  colors: ThemeColors,
): Theme {
  const primary = {
    main: colors.primary,
    contrastText: colors.primaryFg,
  };

  const secondary = {
    light: colors.secondary,
    main: colors.secondary,
    dark: colors.accent,
    contrastText: colors.secondaryFg,
  };

  const warning = {
    main: colors.warning,
    background: colors.warningBg,
    contrastText: colors.warningFg,
  };

  const destructive = {
    main: colors.destructive,
    background: colors.destructiveBg,
    contrastText: colors.destructiveFg,
  };

  const error = {
    main: colors.error,
    background: colors.errorBg,
    contrastText: colors.errorFg,
  };

  const info = {
    main: colors.info,
    background: colors.infoBg,
    contrastText: colors.infoFg,
  };

  const success = {
    main: colors.success,
    background: colors.successBg,
    contrastText: colors.successFg,
  };

  const background = {
    default: colors.background,
    paper: colors.background,
  };

  const text = {
    primary: colors.foreground,
    secondary: colors.mutedFg,
  };

  const divider = colors.border;

  // Focus ring shared by inputs and buttons: a soft halo instead of a thick
  // brand-colored outline.
  const focusRing = `0 0 0 3px ${alpha(colors.primary, 0.25)}`;

  return createTheme({
    // MUI sets font-family on every Typography, so a body class alone never
    // reached the components; the theme has to name the font itself.
    typography: {
      fontFamily: FONT_SANS_STACK,
      button: { textTransform: "none", fontWeight: 500 },
    },
    shape: { borderRadius: 8 },
    palette: {
      mode,
      primary,
      secondary,
      warning,
      error,
      success,
      info,
      background,
      text,
      divider,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Tell the browser which scheme to paint its own UI in — scrollbars,
          // form controls, spellcheck underlines. Without this Chrome assumes
          // light and renders a near-white scrollbar over the dark app. It
          // follows the theme rather than prefers-color-scheme because the mode
          // is an explicit in-app toggle, not an OS setting.
          ":root": { colorScheme: mode },
        },
      },
      MuiChip: {
        styleOverrides: {
          // Vivid, hue-accurate status chips. Filled chips use the full `main`
          // color so green reads green and red reads red (a low-opacity tint
          // over the page background desaturates toward pastel). Text is picked
          // per color via getContrastText (white or dark) to keep WCAG AA.
          // Outlined chips keep a transparent body with a solid-color border +
          // readable text. `default` and `primary` keep MUI's defaults so the
          // brand colors render unchanged. `secondary` keeps its brand fill but
          // gets a visible outline (its pale `main` is invisible as a border).
          root: ({ ownerState, theme }) => {
            const color = ownerState.color;
            if (!color || color === "default" || color === "primary") return {};

            const pc = theme.palette[color];
            const isLight = theme.palette.mode === "light";
            const iconColor = {
              "& .MuiChip-icon, & .MuiChip-deleteIcon": { color: "inherit" },
            };

            if (color === "secondary") {
              // Filled secondary keeps MUI's brand styling; only the outlined
              // border/text need help to be visible.
              if (ownerState.variant !== "outlined") return {};
              return {
                ...iconColor,
                backgroundColor: "transparent",
                borderColor: isLight
                  ? darken(pc.main, 0.4)
                  : lighten(pc.main, 0.4),
                color: isLight ? darken(pc.main, 0.6) : lighten(pc.main, 0.6),
              };
            }

            if (ownerState.variant === "outlined") {
              return {
                ...iconColor,
                backgroundColor: "transparent",
                borderColor: alpha(pc.main, 0.7),
                color: isLight ? darken(pc.main, 0.4) : lighten(pc.main, 0.5),
              };
            }

            return {
              ...iconColor,
              backgroundColor: pc.main,
              color: theme.palette.getContrastText(pc.main),
            };
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            color: colors.appBarFg,
            boxShadow: "none",
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: colors.appBarBg,
            "&.appbar--data-plane": {
              borderBottom: `2px solid ${colors.linkFg}`,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background,
          },
        },
      },
      MuiTabs: {
        variants: [
          {
            props: { className: "lang-selector" },
            style: {
              backgroundColor: colors.tabsBg,
              color: colors.tabsFg,
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              ...(colors.tabsInactiveFg && {
                "& .MuiTab-root": {
                  color: colors.tabsInactiveFg,
                  "&.Mui-selected": {
                    color: colors.tabsFg,
                  },
                },
              }),
            },
          },
        ],
      },
      // A table in a Paper reads as one bordered surface; the default elevation
      // shadow (and its dark-mode white overlay) made it a floating gray slab.
      MuiTableContainer: {
        styleOverrides: {
          root: {
            "&.MuiPaper-root": {
              border: `1px solid ${colors.border}`,
              boxShadow: "none",
              borderRadius: 12,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            // Opaque so sticky headers still cover the rows scrolling beneath.
            "& .MuiTableCell-head": {
              backgroundColor: colors.mutedBg,
              color: colors.mutedFg,
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.02em",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: colors.border,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-child > .MuiTableCell-body": {
              borderBottom: 0,
            },
            "&.MuiTableRow-hover:hover, tbody &:hover": {
              backgroundColor: alpha(colors.foreground, 0.04),
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: primary.main,
            "&.Mui-disabled": {
              color: primary.main,
              pointerEvents: "none",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // Dark mode lightens elevated paper with a white gradient, which
          // turns every surface a slightly different muddy gray.
          root: { backgroundImage: "none" },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { border: `1px solid ${colors.border}` },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: colors.card,
            color: colors.cardFg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.input,
            },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline":
              {
                borderColor: colors.mutedFg,
              },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: 1,
            },
            "&.Mui-focused": {
              boxShadow: focusRing,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.9rem",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { textTransform: "none" },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: colors.linkFg,
            "&:hover": {
              color: colors.linkHoverFg,
              textDecoration: "underline",
            },
            textDecoration: "none",
            textDecorationColor: colors.linkFg,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              boxShadow: focusRing,
            },
            "&.MuiButton-containedError": {
              backgroundColor: destructive.main,
              color: destructive.contrastText,
              "&:hover": {
                backgroundColor: destructive.background,
              },
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            color: colors.input,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.sidebarBg,
            color: colors.sidebarFg,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
            boxShadow: "none",
            boxSizing: "border-box",
            "&.sidebar--data-plane": {
              backgroundColor: colors.dataPlaneSidebarBg,
            },
          },
        },
      },
      ...commonTypography,
      ...commonTheme.components,
    },
  });
}
